import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Server, Plus, Trash2, ArrowUpCircle, X, Download, RefreshCw, RotateCw, Pencil, ChevronRight, ChevronDown, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/ui/DataTable';
import { KpiStat, EmptyState } from '@/components/ui/primitives';
import { CopyButton } from '@/components/ui/CopyButton';

/** Where an edge is in the install flow, independent of whether it's online. */
type PairingState = 'paired' | 'awaiting_pairing' | 'code_expired' | 'none';

interface WatchdogState {
  armed: boolean;
  disconnectedForMs: number;
  restartCount: number;
  lastRestartAt: string | null;
}
interface CapacityState {
  devices: number;
  maxDevices: number | null;
  drivers: number;
  maxDrivers: number | null;
  atCapacity: boolean;
}

interface Edge {
  edgeId: string;
  name: string;
  status: string;
  connected: boolean;
  agentVersion: string | null;
  roles: string[];
  capabilities: string[];
  lastSeenAt: string | null;
  pairingState?: PairingState;
  // Live telemetry (null until the agent reports).
  cpuPercent?: number | null;
  memoryUsedMb?: number | null;
  memoryTotalMb?: number | null;
  diskUsedGb?: number | null;
  diskTotalGb?: number | null;
  uptime?: number | null;
  queueSize?: number | null;
  droppedEvents?: number | null;
  visionAvailable?: boolean | null;
  deviceCount?: number | null;
  driverCount?: number | null;
  watchdog?: WatchdogState | null;
  capacity?: CapacityState | null;
}

interface EdgeDevice {
  deviceId: string;
  name: string;
  type: string | null;
  status: string;
  healthScore: number | null;
  driverSlug: string | null;
  lastSeenAt: string | null;
}

/** What provisioning hands back — a pairing code, plus the raw credentials
 *  for the manual .env path. */
interface Creds {
  edgeId: string;
  token: string;
  clientId?: string;
  pairingCode?: string;
  pairingExpiresAt?: string;
  apiBaseUrl?: string;
}

const ROLE_OPTS = ['video-gateway', 'integration-agent', 'log-collector', 'ai-reasoner'];

// Local collection agents (edge nodes). Tenant-admins can provision, upgrade and
// revoke their customer's edges; the list merges persistent state with the live
// connection.
export function EdgesPage() {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [creds, setCreds] = useState<Creds | null>(null);
  const [upgradeFor, setUpgradeFor] = useState<Edge | null>(null);
  const [revokeFor, setRevokeFor] = useState<Edge | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [restartFor, setRestartFor] = useState<Edge | null>(null);
  const [restarting, setRestarting] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['edges'],
    queryFn: async () => (await api.get<{ data: Edge[]; meta: { connected: number } }>('/api/v1/edges')).data,
    refetchInterval: 5000,
    // Keep polling even when the tab isn't focused, so the live view (last-seen,
    // telemetry, connection state) stays fresh without a manual refresh.
    refetchIntervalInBackground: true,
  });
  const edges = data?.data ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ['edges'] });

  const doRevoke = async () => {
    if (!revokeFor) return;
    setRevoking(true);
    try { await api.delete(`/api/v1/edges/${revokeFor.edgeId}`); setRevokeFor(null); refresh(); }
    finally { setRevoking(false); }
  };

  const doRestart = async () => {
    if (!restartFor) return;
    setRestarting(true);
    try { await api.post(`/api/v1/edges/${restartFor.edgeId}/restart`, {}); setRestartFor(null); }
    finally { setRestarting(false); }
  };

  const doRename = async (edgeId: string, name: string) => {
    await api.patch(`/api/v1/edges/${edgeId}`, { name });
    setRenaming(null);
    refresh();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('edges.title')}
        subtitle={t('edges.subtitle')}
        action={<button className="tk-btn-primary" onClick={() => setProvisionOpen(true)}><Plus size={16} /> {t('edges.actions.provision')}</button>}
      />

      {/* How it works — the page wasn't self-explanatory. */}
      <div className="tk-card flex items-start gap-2 p-3 text-xs text-muted">
        <Server size={14} className="mt-0.5 shrink-0 text-primary" />
        <span>{t('edges.help.before')}<b className="text-fg/80">{t('edges.help.edgeNode')}</b>{t('edges.help.middle')}<b className="text-fg/80">{t('edges.help.provision')}</b>{t('edges.help.afterProvision')}<b className="text-ok">{t('edges.help.online')}</b>{t('edges.help.after')}</span>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiStat label={t('edges.kpi.edges')} value={edges.length} tone="info" />
        <KpiStat label={t('edges.kpi.online')} value={edges.filter((e) => e.connected).length} tone="ok" />
        <KpiStat label={t('edges.kpi.offline')} value={edges.filter((e) => !e.connected).length} tone={edges.some((e) => !e.connected) ? 'warning' : 'ok'} />
        <KpiStat label={t('edges.kpi.revoked')} value={edges.filter((e) => e.status === 'revoked').length} tone="muted" />
      </div>

      <div className="tk-card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted">
            <tr className="border-b border-border">
              <th className="w-6 px-2 py-3" />
              <th className="px-4 py-3">{t('edges.columns.edge')}</th>
              <th className="px-4 py-3">{t('edges.columns.status')}</th>
              <th className="px-3 py-3 text-right">{t('edges.columns.cpu')}</th>
              <th className="px-3 py-3 text-right">{t('edges.columns.mem')}</th>
              <th className="px-3 py-3 text-right">{t('edges.columns.queue')}</th>
              <th className="px-3 py-3 text-center">{t('edges.columns.vision')}</th>
              <th className="px-4 py-3">{t('edges.columns.agent')}</th>
              <th className="px-4 py-3">{t('edges.columns.lastSeen')}</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td className="px-4 py-6 text-muted" colSpan={10}>{t('edges.loading')}</td></tr>}
            {!isLoading && edges.length === 0 && (
              <tr><td colSpan={10}><EmptyState icon={Server} title={t('edges.empty.title')} hint={t('edges.empty.hint')} /></td></tr>
            )}
            {edges.map((e) => (
              <EdgeRow
                key={e.edgeId}
                edge={e}
                expanded={expanded === e.edgeId}
                onToggle={() => setExpanded(expanded === e.edgeId ? null : e.edgeId)}
                renaming={renaming === e.edgeId}
                onStartRename={() => setRenaming(e.edgeId)}
                onCancelRename={() => setRenaming(null)}
                onRename={doRename}
                onUpgrade={() => setUpgradeFor(e)}
                onRestart={() => setRestartFor(e)}
                onRevoke={() => setRevokeFor(e)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {provisionOpen && <ProvisionModal onClose={() => setProvisionOpen(false)} onDone={(c) => { setProvisionOpen(false); setCreds(c); refresh(); }} />}
      {creds && <CredsModal creds={creds} onClose={() => { setCreds(null); refresh(); }} onRegenerate={setCreds} />}
      {upgradeFor && <UpgradeModal edge={upgradeFor} onClose={() => setUpgradeFor(null)} onDone={() => { setUpgradeFor(null); refresh(); }} />}

      {revokeFor && (
        <div className="tk-modal-overlay" onClick={() => !revoking && setRevokeFor(null)}>
          <div className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center gap-2 text-base font-semibold"><Trash2 size={16} className="text-critical" /> {t('edges.revokeModal.title')}</div>
            <p className="text-sm text-muted">{t('edges.revokeModal.confirmBefore')}<b className="text-fg">{revokeFor.name}</b>{t('edges.revokeModal.confirmAfter')}</p>
            <div className="mt-4 flex justify-end gap-2">
              <button className="tk-btn-ghost" disabled={revoking} onClick={() => setRevokeFor(null)}>{t('edges.actions.cancel')}</button>
              <button className="tk-btn-primary bg-critical" disabled={revoking} onClick={doRevoke}>{revoking ? t('edges.actions.revoking') : t('edges.actions.revoke')}</button>
            </div>
          </div>
        </div>
      )}

      {restartFor && (
        <div className="tk-modal-overlay" onClick={() => !restarting && setRestartFor(null)}>
          <div className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center gap-2 text-base font-semibold"><RotateCw size={16} className="text-primary" /> {t('edges.restartModal.title')}</div>
            <p className="text-sm text-muted">{t('edges.restartModal.confirmBefore')}<b className="text-fg">{restartFor.name}</b>{t('edges.restartModal.confirmAfter')}</p>
            <p className="mt-2 text-xs text-muted/70">{t('edges.restartModal.hint')}</p>
            <div className="mt-4 flex justify-end gap-2">
              <button className="tk-btn-ghost" disabled={restarting} onClick={() => setRestartFor(null)}>{t('edges.actions.cancel')}</button>
              <button className="tk-btn-primary" disabled={restarting} onClick={doRestart}>{restarting ? t('edges.actions.restarting') : t('edges.actions.restart')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Live-ticking relative time ("just now", "8s ago", "3m ago"). Re-renders every
// second so "last seen" feels live between server updates (the agent heartbeat
// only advances the server value every ~30s). Full timestamp on hover.
function RelativeTime({ iso }: { iso: string | null }) {
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const { t } = useTranslation('pages');
  if (!iso) return <>—</>;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return <>—</>;
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  const label = s < 5 ? t('edges.time.justNow', 'just now')
    : s < 60 ? t('edges.time.secondsAgo', { count: s, defaultValue: '{{count}}s ago' })
      : s < 3600 ? t('edges.time.minutesAgo', { count: Math.floor(s / 60), defaultValue: '{{count}}m ago' })
        : s < 86400 ? t('edges.time.hoursAgo', { count: Math.floor(s / 3600), defaultValue: '{{count}}h ago' })
          : t('edges.time.daysAgo', { count: Math.floor(s / 86400), defaultValue: '{{count}}d ago' });
  return <span title={new Date(iso).toLocaleString()}>{label}</span>;
}

// One edge row + its expandable detail/devices panel. Split out so the devices
// query only mounts when a row is actually expanded (lazy fetch).
function EdgeRow({
  edge: e, expanded, onToggle, renaming, onStartRename, onCancelRename, onRename,
  onUpgrade, onRestart, onRevoke,
}: {
  edge: Edge; expanded: boolean; onToggle: () => void;
  renaming: boolean; onStartRename: () => void; onCancelRename: () => void;
  onRename: (edgeId: string, name: string) => Promise<void>;
  onUpgrade: () => void; onRestart: () => void; onRevoke: () => void;
}) {
  const { t } = useTranslation('pages');
  const [nameDraft, setNameDraft] = useState(e.name);
  const revoked = e.status === 'revoked';
  const wd = e.watchdog;
  const cap = e.capacity;

  return (
    <>
      <tr className={`border-b border-border/50 ${revoked ? 'opacity-50' : ''}`}>
        <td className="px-2 py-3 text-center">
          {!revoked && (
            <button className="text-muted hover:text-fg" onClick={onToggle} title={t('edges.actions.details')}>
              {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button>
          )}
        </td>
        <td className="px-4 py-3">
          {renaming ? (
            <form className="flex items-center gap-1" onSubmit={(ev) => { ev.preventDefault(); if (nameDraft.trim()) void onRename(e.edgeId, nameDraft.trim()); }}>
              <input autoFocus className="tk-input w-40 py-1 text-sm" value={nameDraft} onChange={(ev) => setNameDraft(ev.target.value)} />
              <button type="submit" className="tk-btn-ghost px-1.5 py-1" title={t('edges.actions.save')}><Check size={14} className="text-ok" /></button>
              <button type="button" className="tk-btn-ghost px-1.5 py-1" onClick={onCancelRename}><X size={14} /></button>
            </form>
          ) : (
            <div className="group flex items-center gap-1.5">
              <div>
                <div className="font-medium">{e.name}</div>
                <div className="tk-num text-xs text-muted">{e.edgeId}</div>
              </div>
              {!revoked && (
                <button className="tk-btn-ghost px-1 py-0.5 opacity-0 transition group-hover:opacity-100" title={t('edges.actions.rename')} onClick={() => { setNameDraft(e.name); onStartRename(); }}>
                  <Pencil size={12} />
                </button>
              )}
            </div>
          )}
        </td>
        <td className="px-4 py-3">
          {revoked
            ? <span className="text-muted">{t('edges.status.revoked')}</span>
            : <span className={e.connected ? 'text-ok' : 'text-critical'}>{e.connected ? `● ${t('edges.status.online')}` : `○ ${t('edges.status.offline')}`}</span>}
          {/* Pairing/onboarding hint — only for an edge that has NEVER connected.
              An agent that has been seen (has lastSeenAt) is installed; when it's
              offline it should read as 'offline', not 'not installed yet'. This
              also avoids mislabeling token-provisioned edges (pairingState 'none'
              because they never used a pairing code) as uninstalled. */}
          {!revoked && !e.connected && !e.lastSeenAt && e.pairingState && e.pairingState !== 'paired' && (
            <div className={`text-xs ${e.pairingState === 'awaiting_pairing' ? 'text-warning' : 'text-muted'}`}>
              {t(`edges.pairing.${e.pairingState}`)}
            </div>
          )}
          {/* Watchdog: surface a flapping/wedged agent right in the status cell. */}
          {!revoked && wd && wd.restartCount > 0 && (
            <div className="text-xs text-warning" title={wd.lastRestartAt ? new Date(wd.lastRestartAt).toLocaleString() : ''}>
              {t('edges.watchdog.restarts', { count: wd.restartCount })}
            </div>
          )}
        </td>
        <td className="px-3 py-3 text-right tk-num text-muted">{fmtPct(e.cpuPercent)}</td>
        <td className="px-3 py-3 text-right tk-num text-muted">{fmtMem(e.memoryUsedMb)}</td>
        <td className="px-3 py-3 text-right tk-num">
          <span className={(e.queueSize ?? 0) > 0 ? 'text-warning' : 'text-muted'}>{e.queueSize ?? '—'}</span>
        </td>
        <td className="px-3 py-3 text-center">{visionCell(e.visionAvailable)}</td>
        <td className="px-4 py-3 text-muted">{e.agentVersion ?? '—'}</td>
        <td className="px-4 py-3 text-muted"><RelativeTime iso={e.lastSeenAt} /></td>
        <td className="px-4 py-3">
          {!revoked && (
            <span className="flex items-center justify-end gap-1">
              <button className="tk-btn-ghost px-1.5 py-1" title={t('edges.actions.restart')} disabled={!e.connected} onClick={onRestart}><RotateCw size={14} /></button>
              <button className="tk-btn-ghost px-2 py-1 text-xs" title={t('edges.actions.pushUpgrade')} disabled={!e.connected} onClick={onUpgrade}>
                <ArrowUpCircle size={13} /> {t('edges.actions.upgrade')}
              </button>
              <button className="tk-btn-ghost px-1.5 py-1" title={t('edges.actions.revoke')} onClick={onRevoke}><Trash2 size={14} /></button>
            </span>
          )}
        </td>
      </tr>
      {expanded && !revoked && (
        <tr className="border-b border-border/50 bg-white/[0.02]">
          <td />
          <td colSpan={9} className="px-4 py-3">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-4">
              <Metric label={t('edges.detail.disk')} value={fmtDisk(e.diskUsedGb, e.diskTotalGb)} />
              <Metric label={t('edges.detail.memory')} value={fmtMemPair(e.memoryUsedMb, e.memoryTotalMb)} />
              <Metric label={t('edges.detail.uptime')} value={fmtUptime(e.uptime)} />
              <Metric label={t('edges.detail.dropped')} value={e.droppedEvents ?? '—'} tone={(e.droppedEvents ?? 0) > 0 ? 'warn' : undefined} />
              <Metric label={t('edges.detail.drivers')} value={e.driverCount ?? '—'} />
              <Metric label={t('edges.detail.version')} value={e.agentVersion ?? '—'} />
              {cap && (
                <Metric
                  label={t('edges.detail.capacity')}
                  value={`${cap.devices}${cap.maxDevices ? `/${cap.maxDevices}` : ''} ${t('edges.detail.devicesWord')}`}
                  tone={cap.atCapacity ? 'warn' : undefined}
                />
              )}
              {wd && (
                <Metric
                  label={t('edges.detail.watchdog')}
                  value={wd.armed ? t('edges.watchdog.armed') : t('edges.watchdog.monitoring')}
                />
              )}
            </div>
            <EdgeDevicesPanel edgeId={e.edgeId} agentConnected={e.connected} agentLastSeenAt={e.lastSeenAt} />
          </td>
        </tr>
      )}
    </>
  );
}

function Metric({ label, value, tone }: { label: string; value: React.ReactNode; tone?: 'warn' }) {
  return (
    <div>
      <div className="text-muted/60">{label}</div>
      <div className={`tk-num ${tone === 'warn' ? 'text-warning' : 'text-fg/80'}`}>{value}</div>
    </div>
  );
}

// Lazily fetches the devices an agent covers (only when its row is expanded).
// When the parent AGENT is offline, its child device states are the last snapshot
// it sent before going quiet — the cloud can't verify them live, so we render them
// as STALE (last-known) rather than a confident green "online".
function EdgeDevicesPanel({ edgeId, agentConnected, agentLastSeenAt }: {
  edgeId: string;
  agentConnected: boolean;
  agentLastSeenAt: string | null;
}) {
  const { t } = useTranslation('pages');
  const { data, isLoading } = useQuery({
    queryKey: ['edge-devices', edgeId],
    queryFn: async () => (await api.get<{ data: EdgeDevice[]; meta: { devices: number } }>(`/api/v1/edges/${edgeId}/devices`)).data,
    refetchInterval: 15000,
  });
  const devices = data?.data ?? [];
  const stale = !agentConnected;
  if (isLoading) return <div className="mt-3 text-xs text-muted">{t('edges.devices.loading')}</div>;
  if (devices.length === 0) return <div className="mt-3 text-xs text-muted">{t('edges.devices.none')}</div>;
  return (
    <div className="mt-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted">{t('edges.devices.title', { count: devices.length })}</span>
        {stale && (
          <span className="text-caption text-warning/80">
            {t('edges.status.staleHint')} · <RelativeTime iso={agentLastSeenAt} />
          </span>
        )}
      </div>
      <div className="overflow-hidden rounded-md ring-1 ring-white/10">
        <table className="w-full text-xs">
          <tbody>
            {devices.map((d) => (
              <tr key={d.deviceId} className="border-b border-white/5 last:border-0">
                <td className="px-3 py-1.5">{d.name}</td>
                <td className="px-3 py-1.5 text-muted">{d.type ?? '—'}</td>
                <td className="px-3 py-1.5">
                  {stale ? (
                    // Agent offline → the status is unverifiable; show it as last-known.
                    <span className="text-muted" title={agentLastSeenAt ? new Date(agentLastSeenAt).toLocaleString() : undefined}>
                      ○ {t('edges.status.stale')}{' '}
                      <span className="text-muted/60">({d.status === 'online' ? t('edges.status.online') : t('edges.status.offline')})</span>
                    </span>
                  ) : (
                    <span className={d.status === 'online' ? 'text-ok' : 'text-critical'}>{d.status === 'online' ? `● ${t('edges.status.online')}` : `○ ${t('edges.status.offline')}`}</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right tk-num text-muted">{d.healthScore != null ? `${d.healthScore}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Formatting helpers ───────────────────────────────────────────────────────
function fmtPct(v: number | null | undefined): string { return v == null ? '—' : `${v}%`; }
function fmtMem(mb: number | null | undefined): string { return mb == null ? '—' : `${mb}MB`; }
function fmtMemPair(u: number | null | undefined, total: number | null | undefined): string {
  if (u == null) return '—';
  return total ? `${u} / ${total}MB` : `${u}MB`;
}
function fmtDisk(u: number | null | undefined, total: number | null | undefined): string {
  if (u == null || !total) return '—';
  return `${u} / ${total}GB`;
}
function fmtUptime(sec: number | null | undefined): string {
  if (sec == null) return '—';
  const d = Math.floor(sec / 86400); const h = Math.floor((sec % 86400) / 3600); const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
function visionCell(v: boolean | null | undefined): React.ReactNode {
  if (v == null) return <span className="text-muted">—</span>;
  return v ? <span className="text-ok" title="image analysis available">✓</span> : <span className="text-warning" title="no image analysis (sharp missing)">⚠</span>;
}

function ProvisionModal({ onClose, onDone }: { onClose: () => void; onDone: (c: Creds) => void }) {
  const { t } = useTranslation('pages');
  const [name, setName] = useState('');
  // Roles this agent is authorized for. An agent can hold several — pick all four
  // for a single "whole-site" box, or one role each for split/per-building agents.
  const [roles, setRoles] = useState<string[]>(['video-gateway']);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const toggleRole = (r: string) =>
    setRoles((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));
  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (roles.length === 0) { setErr(t('edges.provisionModal.roleRequired')); return; }
    setBusy(true); setErr(null);
    try {
      // Send the selected roles; the cloud derives the matching capabilities.
      const r = await api.post<{ data: Creds }>('/api/v1/edges', { name: name || undefined, roles });
      onDone(r.data.data);
    } catch {
      setErr(t('edges.provisionModal.error'));
    } finally { setBusy(false); }
  };
  return (
    <div className="tk-modal-overlay" onClick={() => !busy && onClose()}>
      <form className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="mb-3 flex items-center justify-between"><h2 className="text-base font-semibold">{t('edges.provisionModal.title')}</h2><button type="button" className="tk-btn-ghost px-2" onClick={onClose}><X size={16} /></button></div>
        <p className="mb-3 text-xs text-muted">{t('edges.provisionModal.description')}</p>
        <label className="text-xs text-muted">{t('edges.provisionModal.nameLabel')}<input className="tk-input mt-1 w-full py-1.5 text-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('edges.provisionModal.namePlaceholder')} /></label>
        <fieldset className="mt-3">
          <legend className="text-xs text-muted">{t('edges.provisionModal.rolesLabel')}</legend>
          <p className="mb-2 mt-0.5 text-xs text-muted/70">{t('edges.provisionModal.rolesHint')}</p>
          <div className="space-y-1.5">
            {ROLE_OPTS.map((r) => (
              <label key={r} className="flex cursor-pointer items-start gap-2 rounded-md p-2 ring-1 ring-white/10 hover:bg-white/5">
                <input type="checkbox" className="mt-0.5" checked={roles.includes(r)} onChange={() => toggleRole(r)} />
                <span>
                  <span className="block text-sm font-medium">{t(`edges.roles.${r}.label`)}</span>
                  <span className="block text-xs text-muted/70">{t(`edges.roles.${r}.desc`)}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        {busy && <p className="mt-3 flex items-center gap-2 text-xs text-muted"><span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" /> {t('edges.provisionModal.creating')}</p>}
        {err && <p className="mt-3 text-xs text-critical">{err}</p>}
        <div className="mt-4 flex justify-end gap-2"><button type="button" className="tk-btn-ghost" disabled={busy} onClick={onClose}>{t('edges.actions.cancel')}</button><button type="submit" className="tk-btn-primary" disabled={busy || roles.length === 0}>{busy ? t('edges.actions.provisioning') : t('edges.actions.provision')}</button></div>
      </form>
    </div>
  );
}

/** The edge.conf an operator downloads and drops next to the agent. */
function buildEdgeConf(creds: Creds): string {
  return [
    '# IOSentra edge agent — pairing file.',
    '# Put this next to the agent and start it. The agent exchanges the code',
    '# below for its real credentials, and the code stops working immediately',
    '# afterwards — so this file is safe to discard once the edge is online.',
    `# Generated for ${creds.edgeId}.`,
    '',
    `PAIRING_CODE=${creds.pairingCode ?? ''}`,
    `CLOUD_API_URL=${creds.apiBaseUrl ?? ''}`,
    '',
  ].join('\n');
}

function downloadEdgeConf(creds: Creds): void {
  const url = URL.createObjectURL(new Blob([buildEdgeConf(creds)], { type: 'text/plain' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'edge.conf';
  link.click();
  // Revoke on the next tick — revoking synchronously can cancel the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Countdown so the operator knows how long the code is good for. */
function useCountdown(expiresAt?: string): number {
  const [left, setLeft] = useState(() => remainingSeconds(expiresAt));
  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => setLeft(remainingSeconds(expiresAt)), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  return left;
}

function remainingSeconds(expiresAt?: string): number {
  if (!expiresAt) return 0;
  return Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
}

function CredsModal({ creds, onClose, onRegenerate }: { creds: Creds; onClose: () => void; onRegenerate: (c: Creds) => void }) {
  const { t } = useTranslation('pages');
  // The manual .env route stays available for CI/advanced installs, but it is
  // collapsed: pairing is the path we want operators to take.
  const [showManual, setShowManual] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const secondsLeft = useCountdown(creds.pairingExpiresAt);
  const expired = !!creds.pairingExpiresAt && secondsLeft === 0;

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const r = await api.post<{ data: Creds }>(`/api/v1/edges/${creds.edgeId}/pairing-code`, {});
      onRegenerate({ ...creds, ...r.data.data });
    } finally { setRegenerating(false); }
  };

  const mmss = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  return (
    <div className="tk-modal-overlay" onClick={onClose}>
      <div className="tk-modal w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 text-base font-semibold">{t('edges.credsModal.title')}</div>
        <p className="text-sm text-muted">{t('edges.credsModal.pairIntro')}</p>

        {creds.pairingCode && (
          <div className={`mt-3 rounded-md p-3 ring-1 ${expired ? 'bg-critical/10 ring-critical/30' : 'bg-warning/10 ring-warning/30'}`}>
            <div className="text-xs text-muted">{t('edges.credsModal.pairingCodeLabel')}</div>
            <div className="mt-1 flex items-center gap-2">
              <span className={`tk-num select-all text-xl font-semibold tracking-widest ${expired ? 'text-critical line-through' : 'text-warning'}`}>
                {creds.pairingCode}
              </span>
              {!expired && <CopyButton value={creds.pairingCode} />}
            </div>
            <div className="mt-1.5 text-xs text-muted">
              {expired
                ? t('edges.credsModal.codeExpired')
                : t('edges.credsModal.expiresIn', { time: mmss })}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button className="tk-btn-ghost text-xs" onClick={() => downloadEdgeConf(creds)} disabled={expired}>
                <Download size={14} /> {t('edges.credsModal.downloadConf')}
              </button>
              <button className="tk-btn-ghost text-xs" onClick={regenerate} disabled={regenerating}>
                <RefreshCw size={14} className={regenerating ? 'animate-spin' : ''} /> {t('edges.credsModal.regenerate')}
              </button>
            </div>
          </div>
        )}

        <ol className="mt-3 list-decimal space-y-1 pl-5 text-xs text-muted">
          <li>{t('edges.credsModal.step1')}</li>
          <li>{t('edges.credsModal.step2')}</li>
          <li>{t('edges.credsModal.step3')}</li>
        </ol>

        <button className="mt-3 text-xs text-muted underline hover:text-fg" onClick={() => setShowManual((v) => !v)}>
          {showManual ? t('edges.credsModal.hideManual') : t('edges.credsModal.showManual')}
        </button>
        {showManual && (
          <div className="mt-2 space-y-2 rounded-md bg-white/5 p-3 text-sm ring-1 ring-white/10">
            <p className="text-xs text-muted">{t('edges.credsModal.instructionBefore')}<b className="text-warning">{t('edges.credsModal.tokenOnce')}</b>{t('edges.credsModal.instructionAfter')}</p>
            <div><span className="text-muted">EDGE_ID</span><div className="tk-num select-all font-medium">{creds.edgeId}</div></div>
            {creds.clientId && <div><span className="text-muted">CLIENT_ID</span><div className="tk-num select-all font-medium">{creds.clientId}</div></div>}
            <div><span className="text-muted">AUTH_TOKEN</span><div className="tk-num select-all font-semibold text-warning">{creds.token}</div></div>
            <div><span className="text-muted">CLOUD_URL</span><div className="tk-num select-all">ws://&lt;cloud-host&gt;:4000/ws/edge</div></div>
          </div>
        )}

        <button className="tk-btn-primary mt-4 w-full justify-center" onClick={onClose}>{t('edges.actions.done')}</button>
      </div>
    </div>
  );
}

function UpgradeModal({ edge, onClose, onDone }: { edge: Edge; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('pages');
  const [target, setTarget] = useState<'agent' | 'driver'>('agent');
  const [version, setVersion] = useState('');
  const [driverSlug, setDriverSlug] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault(); setBusy(true); setErr(null);
    try {
      await api.post(`/api/v1/edges/${edge.edgeId}/upgrade`, { target, version: version || undefined, driverSlug: target === 'driver' ? driverSlug || undefined : undefined });
      onDone();
    } catch { setErr(t('edges.upgradeModal.error')); } finally { setBusy(false); }
  };
  return (
    <div className="tk-modal-overlay" onClick={onClose}>
      <form className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="mb-3 text-base font-semibold">{t('edges.upgradeModal.title', { name: edge.name })}</div>
        <label className="text-xs text-muted">{t('edges.upgradeModal.targetLabel')}
          <select className="tk-input mt-1 w-full py-1.5 text-sm" value={target} onChange={(e) => setTarget(e.target.value as 'agent' | 'driver')}>
            <option value="agent">{t('edges.upgradeModal.targetAgent')}</option><option value="driver">{t('edges.upgradeModal.targetDriver')}</option>
          </select>
        </label>
        {target === 'driver' && <label className="mt-3 block text-xs text-muted">{t('edges.upgradeModal.driverSlugLabel')}<input className="tk-input mt-1 w-full py-1.5 text-sm" value={driverSlug} onChange={(e) => setDriverSlug(e.target.value)} placeholder="dahua-nvr" /></label>}
        <label className="mt-3 block text-xs text-muted">{t('edges.upgradeModal.versionLabel')}<input className="tk-input mt-1 w-full py-1.5 text-sm" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.1.0" /></label>
        {err && <div className="mt-2 text-xs text-critical">{err}</div>}
        <div className="mt-4 flex justify-end gap-2"><button type="button" className="tk-btn-ghost" onClick={onClose}>{t('edges.actions.cancel')}</button><button type="submit" className="tk-btn-primary" disabled={busy}>{busy ? t('edges.actions.pushing') : t('edges.actions.pushUpgrade')}</button></div>
      </form>
    </div>
  );
}
